import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Undo, Redo, Trash2, Share2, Save, Upload, FileImage, Cloud, Copy } from 'lucide-react';
import { TL4KAppHeader } from '@tl4k/admin-ui';
import Canvas from './Canvas';
import PropertyPanel from './PropertyPanel';
import { useEditorStore, createDefaultProject } from '../stores/editorStore';
import type { Project, Hotspot } from '../types';

import { processFile } from '../utils/fileUtils';

function Editor() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const {
    project,
    selectedElementId,
    canUndo,
    canRedo,
    undo,
    redo,
    deleteElement,
    selectElement,
    setProject,
    loadProject,
    duplicateElement,
  } = useEditorStore();

  const [isSaving, setIsSaving] = useState(false);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [dragError, setDragError] = useState<string | null>(null);

  // Drag & drop handlers
  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only remove if leaving the container (not entering a child)
    if ((e.target as HTMLElement).classList.contains('h-screen')) {
      setIsDraggingFile(false);
    }
  }, []);

  // Initialize project: try to load from storage first, then create default
  useEffect(() => {
    if (!project && projectId) {
      console.log('Editor: Loading project', projectId);
      // Try to load from storage first
      const loaded = loadProject(projectId);
      console.log('Editor: Project loaded from storage:', loaded);

      if (!loaded) {
        console.log('Editor: Creating new default project');
        // If not found in storage, create a new default project
        const defaultProject = createDefaultProject();
        // Override the ID with the URL parameter
        const newProject = { ...defaultProject, id: projectId };
        setProject(newProject);
      }
    }
  }, [project, projectId, setProject, loadProject]);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);
    setDragError(null);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const file = files[0]; // Handle single file

    try {
      const result = await processFile(file);
      if (result && project) {
        const newProject: Project = {
          ...project,
          backgroundImage: result.image,
          backgroundImageType: result.type,
          width: result.width,
          height: result.height,
          updatedAt: new Date(),
        };
        setProject(newProject);
      }
    } catch (error) {
      setDragError(error instanceof Error ? error.message : 'Errore nel caricamento del file');
    }
  }, [project, setProject]);

  const handlePublish = async () => {
    setIsSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsSaving(false);
    alert('Pubblicazione completata! Link copiato negli appunti.');
  };

  const handleSave = async () => {
    setIsSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 500));
    setIsSaving(false);
    alert('Progetto salvato!');
  };

  return (
    <div
      className="h-screen flex flex-col bg-surface-warm"
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* TL4K Standard Header */}
      <TL4KAppHeader
        appName="Magic Page"
        surface="neutral"
        onBackToApp={() => navigate('/')}
        backLabel="Dashboard"
      />
      {/* Drag & drop overlay */}
      {isDraggingFile && (
        <div className="fixed inset-0 bg-primary-600/90 z-50 flex items-center justify-center">
          <div className="text-center text-white">
            <FileImage className="w-16 h-16 mx-auto mb-4" />
            <p className="text-2xl font-semibold">Rilascia il file qui</p>
            <p className="text-primary-200 mt-2">Immagini (PNG, JPG, SVG) o PDF</p>
          </div>
        </div>
      )}

      {/* Drag error toast */}
      {dragError && (
        <div className="fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50">
          {dragError}
        </div>
      )}
      <header className="bg-white border-b px-4 py-2 flex items-center justify-between z-10">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Torna alla dashboard"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="font-semibold text-gray-800">{project?.name || 'Editor'}</h1>
            <p className="text-xs text-gray-500 flex items-center gap-1">
              {project?.elements.length || 0} elementi
              {project && (
                <>
                  <span>•</span>
                  <Cloud className="w-3 h-3 text-green-500" />
                  <span className="text-green-600">Salvato</span>
                  <span>{project.updatedAt.toLocaleTimeString('it')}</span>
                </>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1">
          {/* Upload button */}
          <label className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer" title="Carica file (o trascina qui)">
            <Upload className="w-5 h-5 text-gray-600" />
            <input
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) {
                  try {
                    const result = await processFile(file);
                    if (result && project) {
                      const newProject: Project = {
                        ...project,
                        backgroundImage: result.image,
                        backgroundImageType: result.type,
                        width: result.width,
                        height: result.height,
                        updatedAt: new Date(),
                      };
                      setProject(newProject);
                    }
                  } catch (error) {
                    alert(error instanceof Error ? error.message : 'Errore nel caricamento');
                  }
                }
              }}
            />
          </label>
          <button
            onClick={undo}
            disabled={!canUndo()}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Annulla (Ctrl+Z)"
          >
            <Undo className="w-5 h-5 text-gray-600" />
          </button>
          <button
            onClick={redo}
            disabled={!canRedo()}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Ripristina (Ctrl+Shift+Z)"
          >
            <Redo className="w-5 h-5 text-gray-600" />
          </button>

          <div className="w-px h-6 bg-gray-200 mx-2" />

          <button
            onClick={() => selectedElementId && deleteElement(selectedElementId)}
            disabled={!selectedElementId}
            className="p-2 hover:bg-red-50 text-red-500 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Elimina elemento"
          >
            <Trash2 className="w-5 h-5" />
          </button>

          <div className="w-px h-6 bg-gray-200 mx-2" />

          <button
            onClick={handleSave}
            disabled={isSaving}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
            title="Salva progetto"
          >
            <Save className="w-5 h-5 text-gray-600" />
            <span className="text-sm text-gray-600">{isSaving ? 'Salvataggio...' : 'Salva'}</span>
          </button>

          <button
            onClick={() => project && navigate(`/player/${project.id}`)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2 group"
            title="Anteprima Player"
          >
            <span className="text-sm text-gray-600 group-hover:text-primary-600">Anteprima</span>
          </button>

          <button
            onClick={handlePublish}
            disabled={isSaving}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <Share2 className="w-5 h-5" />
            <span className="text-sm">Pubblica</span>
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        <aside className="w-64 border-r bg-white overflow-y-auto">
          <div className="p-4 border-b">
            <h2 className="font-medium text-gray-800">Elementi</h2>
            <p className="text-xs text-gray-500 mt-1">
              Doppio click sull'immagine per aggiungere un hotspot
            </p>
          </div>
          <div className="p-2">
            {project?.elements.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                Nessun elemento ancora.<br />
                Aggiungi il primo hotspot!
              </div>
            ) : (
              <ul className="space-y-1">
                {project?.elements.map((element, index) => (
                  <li
                    key={element.id}
                    onClick={() => selectElement(element.id)}
                    className={`p-2 rounded-lg cursor-pointer transition-colors group ${selectedElementId === element.id
                      ? 'bg-primary-50 text-primary-700'
                      : 'hover:bg-gray-50 text-gray-700'
                      }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${element.type === 'hotspot' ? 'bg-red-500' : 'bg-blue-500'
                        }`} />
                      <span className="text-sm truncate flex-1">
                        {(element as Hotspot).title || `Hotspot ${index + 1}`}
                      </span>
                      {/* Action buttons - visible on hover or when selected */}
                      <div className={`flex items-center gap-1 ${selectedElementId === element.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            duplicateElement(element.id);
                          }}
                          className="p-1 hover:bg-white hover:shadow rounded transition-all"
                          title="Duplica"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteElement(element.id);
                          }}
                          className="p-1 hover:bg-red-100 hover:text-red-600 rounded transition-all"
                          title="Elimina"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        <main className="flex-1 relative overflow-hidden">
          {project ? (
            <Canvas
              imageUrl={project.backgroundImage}
              width={project.width}
              height={project.height}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <div className="text-center">
                <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-gray-500">Caricamento...</p>
              </div>
            </div>
          )}
        </main>

        <aside className="w-80 border-l bg-white overflow-y-auto">
          {project && <PropertyPanel key={project.id} />}
        </aside>
      </div>
    </div>
  );
}

export default Editor;
